<template>
  <div class="hh-import-page">
    <div v-if="isInitialLoading" class="hh-import-loader-container">
      <Preloader />
    </div>

    <div v-else-if="allVacanciesImported" class="hh-all-imported-container">
      <div class="hh-all-imported-card">
        <img src="@/assets/img/check.svg" alt="check" class="hh-all-imported-icon" />
        <h2 class="hh-all-imported-title">
          {{ $t('allImportedTitle') }}
        </h2>
        <p class="hh-all-imported-text">
          {{ $t('allImportedText') }} <router-link :to="{ name: 'vacancies' }" class="hh-all-imported-link">
            <span>{{ allImportedLinkText }}</span>
          </router-link> {{ $t('allImportedTextSuffix') }}
        </p>
      </div>
    </div>

    <template v-else>
      <div class="hh-import-header-section">
        <h1 class="hh-import-title">
          {{ $t('pageTitle') }}
        </h1>
        <div class="hh-import-buttons">
          <VBtn
            color="success"
            :disabled="selectedVacancies.length === 0 || loading"
            :loading="importingSelected"
            @click="importSelectedVacancies"
            class="hh-import-btn"
          >
            {{ $t('importSelected') }}
          </VBtn>
          <VBtn
            color="success"
            :disabled="loading || !hasAvailableVacancies"
            :loading="importingAll"
            @click="importAllVacancies"
            class="hh-import-btn"
          >
            {{ $t('importAll') }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="loading"
            @click="showAttachDialog = true"
            class="hh-import-btn"
          >
            {{ $t('attachToExisting') }}
          </VBtn>
        </div>
      </div>

      <div class="hh-import-mobile-info">
        <div class="hh-import-mobile-info-content" :class="{ 'hh-import-mobile-info-expanded': showMobileInfoExpanded }">
          <div class="hh-info-section">
            <h3 class="hh-info-section-title">
              {{ $t('importSelectedSectionTitle') }}
            </h3>
            <p class="hh-info-section-text">
              {{ $t('importSelectedSectionDescription') }}
            </p>
            <p class="hh-info-section-instruction">
              {{ $t('importSelectedSectionInstruction') }}
            </p>
          </div>

          <div class="hh-info-section">
            <h3 class="hh-info-section-title">
              {{ $t('importAllSectionTitle') }}
            </h3>
            <p class="hh-info-section-text">
              {{ $t('importAllSectionDescription') }}
            </p>
          </div>

          <p class="hh-info-general-text">
            {{ $t('generalStatement') }}
          </p>

          <div class="hh-info-section">
            <h3 class="hh-info-section-title">
              {{ $t('generalInfoTitle') }}
            </h3>
            <ul class="hh-info-list">
              <li class="hh-info-list-item">
                {{ $t('generalInfoPoint1') }}
              </li>
              <li class="hh-info-list-item">
                {{ $t('generalInfoPoint2') }}
              </li>
              <li class="hh-info-list-item">
                {{ $t('generalInfoPoint3') }}
              </li>
              <li class="hh-info-list-item">
                {{ $t('generalInfoPoint4') }}
              </li>
            </ul>
          </div>
          <div class="hh-import-mobile-info-gradient" />
          <div class="hh-import-mobile-info-button-wrapper">
            <VBtn
              @click="showMobileInfoExpanded = !showMobileInfoExpanded"
              class="hh-import-mobile-info-button"
            >
              {{ showMobileInfoExpanded ? $t('showLess') : $t('showMore') }}
            </VBtn>
          </div>
        </div>
      </div>

      <VRow class="hh-import-content">
        <VCol cols="12" md="6" class="hh-import-left-col">
          <VCard class="hh-import-card">
            <VCardTitle class="hh-import-card-title">
              {{ $t('selectVacanciesTitle') }}
            </VCardTitle>
            <VCardText v-if="errorMessage">
              <VAlert type="error">
                {{ errorMessage }}
              </VAlert>
            </VCardText>
            <VCardText v-else-if="loading">
              <div class="text-center py-8">
                <VProgressCircular indeterminate color="primary" />
              </div>
            </VCardText>
            <template v-else-if="hhVacancies.length">
              <VCardText class="hh-import-select-section">
                <div class="hh-import-select-all">
                  <VCheckbox
                    :indeterminate="checkAllIndeterminate"
                    v-model="checkAll"
                    hide-details
                  >
                    <template v-slot:label>
                      <span>{{ $t('selectAll') }}</span>
                    </template>
                  </VCheckbox>
                </div>
                <div class="hh-import-selected-count">
                  {{ $t('selectedCount', { count: selectedVacancies.length }) }}
                </div>
              </VCardText>

              <div class="hh-vacancy-list">
                <div
                  v-for="vacancy in filteredVacancies"
                  :key="vacancy.id"
                  class="hh-vacancy-item"
                  :class="{ 'hh-vacancy-item-selected': isSelected(vacancy.id), 'hh-vacancy-item-imported': isImported(vacancy) }"
                  @click="toggleVacancySelection(vacancy.id, vacancy, $event)"
                >
                  <VCheckbox
                    v-model="selectedVacancies"
                    :value="vacancy.id"
                    :disabled="isImported(vacancy)"
                    hide-details
                    class="hh-vacancy-checkbox"
                  />
                  <div class="hh-vacancy-info">
                    <div class="hh-vacancy-title">
                      {{ vacancy.name || vacancy.title }}
                    </div>
                    <div class="hh-vacancy-details">
                      <span class="hh-vacancy-detail-item">
                        {{ $t('createdDate') }}: {{ getCreatedDate(vacancy) }}
                      </span>
                      <span v-if="isImported(vacancy)" class="hh-vacancy-imported-text">
                        {{ $t('imported') }}
                      </span>
                      <div v-if="isImported(vacancy)" class="hh-vacancy-imported-icon">
                        <img
                          src="@/assets/img/check.svg"
                          alt="check"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <VCardText v-else>
              <div class="text-center py-8">
                {{ $t('noVacanciesAvailable') }}
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6" class="hh-import-right-col">
          <VCard class="hh-import-info-card">
            <VCardText>
              <div class="hh-info-section">
                <h3 class="hh-info-section-title">
                  {{ $t('importSelectedSectionTitle') }}
                </h3>
                <p class="hh-info-section-text">
                  {{ $t('importSelectedSectionDescription') }}
                </p>
                <p class="hh-info-section-instruction">
                  {{ $t('importSelectedSectionInstruction') }}
                </p>
              </div>

              <div class="hh-info-section">
                <h3 class="hh-info-section-title">
                  {{ $t('importAllSectionTitle') }}
                </h3>
                <p class="hh-info-section-text">
                  {{ $t('importAllSectionDescription') }}
                </p>
              </div>

              <p class="hh-info-general-text">
                {{ $t('generalStatement') }}
              </p>

              <div class="hh-info-section">
                <h3 class="hh-info-section-title">
                  {{ $t('generalInfoTitle') }}
                </h3>
                <ul class="hh-info-list">
                  <li class="hh-info-list-item">
                    {{ $t('generalInfoPoint1') }}
                  </li>
                  <li class="hh-info-list-item">
                    {{ $t('generalInfoPoint2') }}
                  </li>
                  <li class="hh-info-list-item">
                    {{ $t('generalInfoPoint3') }}
                  </li>
                  <li class="hh-info-list-item">
                    {{ $t('generalInfoPoint4') }}
                  </li>
                </ul>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <VDialog
      v-model="showAttachDialog"
      max-width="800"
      persistent
    >
      <VCard>
        <VCardTitle class="headline">
          {{ $t('attachToExistingTitle') }}
        </VCardTitle>
        <VCardText>
          <VAlert
            v-if="attachError"
            type="error"
            class="mb-4"
          >
            {{ attachError }}
          </VAlert>
          <VRow>
            <VCol cols="12" md="6">
              <VSelect
                v-model="selectedCabinetVacancy"
                :items="availableCabinetVacancies"
                :label="$t('selectCabinetVacancy')"
                item-text="title"
                item-value="id"
                :error-messages="cabinetVacancyError"
                clearable
              />
            </VCol>
            <VCol cols="12" md="6">
              <VSelect
                v-model="selectedHHVacancy"
                :items="availableHHVacancies"
                :label="$t('selectHHVacancy')"
                item-text="name"
                item-value="id"
                :error-messages="hhVacancyError"
                clearable
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            text
            @click="closeAttachDialog"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!selectedCabinetVacancy || !selectedHHVacancy || attaching"
            :loading="attaching"
            @click="handleAttachToExisting"
          >
            {{ $t('attach') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<script>
import useHHIntegration from '@/compositions/useHHIntegration';
import useVacancies from '@/compositions/useVacancies';
import Preloader from '@/components/Preloader.vue';
import { format } from '@/date-utils';
import {
  ref, computed, watch, onMounted, nextTick,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { settingsTabs, accountTabs } from '@/enums';

export default {
  name: 'HHVacanciesImport',
  components: {
    Preloader,
  },
  setup(props, { root }) {
    const hhIntegration = useHHIntegration();
    const { vacancies: cabinetVacancies, fetch: fetchCabinetVacancies } = useVacancies();
    const { t } = useI18n();

    const selectedVacancies = ref([]);
    const checkAll = ref(false);
    const importingSelected = ref(false);
    const importingAll = ref(false);
    const importSuccess = ref(false);
    const isInitialLoading = ref(true);
    const showMobileInfoExpanded = ref(false);
    const showAttachDialog = ref(false);
    const selectedCabinetVacancy = ref(null);
    const selectedHHVacancy = ref(null);
    const attaching = ref(false);
    const attachError = ref(null);

    const hhVacancies = computed(() => {
      return hhIntegration.hhVacancies.value || [];
    });

    const loading = computed(() => {
      return hhIntegration.loading.value || false;
    });

    const errorMessage = computed(() => {
      return hhIntegration.errorMessage.value;
    });

    const filteredVacancies = computed(() => {
      return hhVacancies.value;
    });

    const allVacanciesImported = computed(() => {
      if (!hhVacancies.value || hhVacancies.value.length === 0) {
        return false;
      }
      return hhVacancies.value.every((hhVacancy) => isImported(hhVacancy));
    });

    const hasAvailableVacancies = computed(() => {
      if (!hhVacancies.value || hhVacancies.value.length === 0) {
        return false;
      }
      return hhVacancies.value.some((hhVacancy) => !isImported(hhVacancy));
    });

    const allImportedLinkText = computed(() => {
      return t('allImportedLink').trim();
    });

    const importSuccessLinkText = computed(() => {
      return t('importSuccessLink').trim();
    });

    const availableCabinetVacancies = computed(() => {
      if (!cabinetVacancies.value) {
        return [];
      }
      return cabinetVacancies.value.filter((vacancy) => {
        return !vacancy.hasHhIntegration;
      });
    });

    const availableHHVacancies = computed(() => {
      if (!hhVacancies.value) {
        return [];
      }
      return hhVacancies.value.filter((hhVacancy) => {
        return !isImported(hhVacancy);
      });
    });

    const cabinetVacancyError = computed(() => {
      if (!selectedCabinetVacancy.value) {
        return null;
      }
      const vacancy = cabinetVacancies.value?.find((v) => v.id === selectedCabinetVacancy.value);
      if (vacancy?.hasHhIntegration) {
        return t('vacancyAlreadyHasIntegration');
      }
      return null;
    });

    const hhVacancyError = computed(() => {
      if (!selectedHHVacancy.value) {
        return null;
      }
      const hhVacancy = hhVacancies.value?.find((v) => v.id === selectedHHVacancy.value);
      if (hhVacancy && isImported(hhVacancy)) {
        return t('hhVacancyAlreadyAttached');
      }
      return null;
    });

    const isSelected = (vacancyId) => {
      return selectedVacancies.value.includes(vacancyId);
    };

    const toggleVacancySelection = (vacancyId, vacancy, event) => {
      if (isImported(vacancy)) {
        return;
      }

      if (event) {
        const { target } = event;
        if (target.closest('.hh-vacancy-checkbox')
            || target.closest('.v-input--selection-controls')
            || target.closest('.v-input--checkbox')
            || target.tagName === 'INPUT'
            || target.closest('input[type="checkbox"]')) {
          return;
        }
      }

      const index = selectedVacancies.value.indexOf(vacancyId);
      if (index > -1) {
        selectedVacancies.value.splice(index, 1);
      } else {
        selectedVacancies.value.push(vacancyId);
      }
    };

    const isImported = (hhVacancy) => {
      if (!cabinetVacancies.value || !hhVacancy || !hhVacancy.name) {
        return false;
      }

      const hhVacancyName = String(hhVacancy.name).trim();

      return cabinetVacancies.value.some((cabinetVacancy) => {
        if (cabinetVacancy.hasHhIntegration !== true) {
          return false;
        }

        const cabinetVacancyTitle = cabinetVacancy.title ? String(cabinetVacancy.title).trim() : null;
        if (!cabinetVacancyTitle) {
          return false;
        }

        return cabinetVacancyTitle === hhVacancyName;
      });
    };

    const checkAllIndeterminate = computed(() => {
      const selectableVacancies = filteredVacancies.value.filter((v) => !isImported(v));
      return selectedVacancies.value.length > 0
        && selectedVacancies.value.length !== selectableVacancies.length;
    });

    const getCreatedDate = (vacancy) => {
      const date = vacancy.created_at || vacancy.createdAt || vacancy.created;
      if (!date) {
        return t('dateNotAvailable');
      }
      try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return format(dateObj, 'dd.MM.yyyy');
      } catch (e) {
        return String(date);
      }
    };

    const importSelectedVacancies = async () => {
      if (selectedVacancies.value.length === 0) {
        return;
      }

      importingSelected.value = true;
      try {
        await hhIntegration.importVacancies(selectedVacancies.value);
        const errorMsg = hhIntegration.errorMessage.value;
        if (!errorMsg) {
          await Promise.all([
            hhIntegration.fetchVacancies(),
            fetchCabinetVacancies(),
          ]);
          const count = selectedVacancies.value.length;
          selectedVacancies.value = [];

          await nextTick();

          if (allVacanciesImported.value) {
            root.$toast.success(t('allVacanciesImported'), { x: 'right', y: 'top' });
          } else if (count === 1) {
            root.$toast.success(t('vacancyImported'), { x: 'right', y: 'top' });
          } else {
            root.$toast.success(t('vacanciesImported'), { x: 'right', y: 'top' });
          }

          await nextTick();
          root.$router.push({
            name: 'settings',
            params: {
              type: settingsTabs.ACCOUNT,
              addType: accountTabs.SETTINGS,
            },
          });
        }
      } catch (error) {
        root.$toast.error(error.message || t('errorImportingVacancies'), { x: 'right', y: 'top' });
      } finally {
        importingSelected.value = false;
      }
    };

    const importAllVacancies = async () => {
      importingAll.value = true;
      try {
        await hhIntegration.importAllVacancies();
        const errorMsg = hhIntegration.errorMessage.value;
        if (!errorMsg) {
          await Promise.all([
            hhIntegration.fetchVacancies(),
            fetchCabinetVacancies(),
          ]);
          selectedVacancies.value = [];

          await nextTick();

          if (allVacanciesImported.value) {
            root.$toast.success(t('allVacanciesImported'), { x: 'right', y: 'top' });
          }

          await nextTick();
          root.$router.push({
            name: 'settings',
            params: {
              type: settingsTabs.ACCOUNT,
              addType: accountTabs.SETTINGS,
            },
          });
        }
      } catch (error) {
        root.$toast.error(error.message || t('errorImportingVacancies'), { x: 'right', y: 'top' });
      } finally {
        importingAll.value = false;
      }
    };

    const closeAttachDialog = () => {
      showAttachDialog.value = false;
      selectedCabinetVacancy.value = null;
      selectedHHVacancy.value = null;
      attachError.value = null;
    };

    const handleAttachToExisting = async () => {
      if (!selectedCabinetVacancy.value || !selectedHHVacancy.value) {
        return;
      }

      const cabinetVacancy = cabinetVacancies.value?.find((v) => v.id === selectedCabinetVacancy.value);
      if (cabinetVacancy?.hasHhIntegration) {
        attachError.value = t('vacancyAlreadyHasIntegration');
        return;
      }

      const hhVacancy = hhVacancies.value?.find((v) => v.id === selectedHHVacancy.value);
      if (hhVacancy && isImported(hhVacancy)) {
        attachError.value = t('hhVacancyAlreadyAttached');
        return;
      }

      attaching.value = true;
      attachError.value = null;

      try {
        await hhIntegration.attachToExisting(selectedCabinetVacancy.value, selectedHHVacancy.value);
        const errorMsg = hhIntegration.errorMessage.value;
        if (!errorMsg) {
          await Promise.all([
            hhIntegration.fetchVacancies(),
            fetchCabinetVacancies(),
          ]);
          root.$toast.success(t('vacancyAttachedSuccessfully'), { x: 'right', y: 'top' });
          closeAttachDialog();

          await nextTick();
          root.$router.push({
            name: 'settings',
            params: {
              type: settingsTabs.ACCOUNT,
              addType: accountTabs.SETTINGS,
            },
          });
        } else {
          attachError.value = errorMsg;
        }
      } catch (error) {
        attachError.value = error.message || t('errorAttachingVacancy');
      } finally {
        attaching.value = false;
      }
    };

    watch(checkAll, (newVal) => {
      const selectableVacancies = filteredVacancies.value.filter((v) => !isImported(v));
      if (newVal) {
        selectedVacancies.value = selectableVacancies.map((v) => v.id);
      } else {
        selectedVacancies.value = [];
      }
    });

    watch(selectedVacancies, (newVal) => {
      const selectableVacancies = filteredVacancies.value.filter((v) => !isImported(v));
      checkAll.value = newVal.length === selectableVacancies.length && selectableVacancies.length > 0;
    });

    onMounted(async () => {
      try {
        isInitialLoading.value = true;
        await Promise.all([
          hhIntegration.fetchVacancies(),
          fetchCabinetVacancies(),
        ]);
      } catch (error) {
      } finally {
        isInitialLoading.value = false;
      }
    });

    return {
      selectedVacancies,
      checkAll,
      importingSelected,
      importingAll,
      importSuccess,
      isInitialLoading,
      hhVacancies,
      loading,
      errorMessage,
      filteredVacancies,
      checkAllIndeterminate,
      allVacanciesImported,
      hasAvailableVacancies,
      isSelected,
      isImported,
      toggleVacancySelection,
      getCreatedDate,
      importSelectedVacancies,
      importAllVacancies,
      showMobileInfoExpanded,
      allImportedLinkText,
      importSuccessLinkText,
      showAttachDialog,
      selectedCabinetVacancy,
      selectedHHVacancy,
      attaching,
      attachError,
      availableCabinetVacancies,
      availableHHVacancies,
      cabinetVacancyError,
      hhVacancyError,
      closeAttachDialog,
      handleAttachToExisting,
    };
  },
  metaInfo() {
    return {
      title: this.$t('importVacanciesFromHH'),
    };
  },
};
</script>

<style lang="scss" scoped>
.hh-import-success-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 10rem);
  padding: 2rem;
}

.hh-import-success-card {
  max-width: 420px;
  width: 100%;
  border-radius: 0.625rem;
  background-color: #f5f5f5;
}

.hh-success-icon {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
}

.hh-success-icon-img {
  width: 50px;
  height: 50px;
  background-color: rgba(31, 197, 127, 0.1);
  border-radius: 50%;
  padding: 12px;
  box-sizing: border-box;
}

.hh-success-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #424242;
  margin-bottom: 1rem;
}

.hh-success-text {
  font-size: 1rem;
  color: #424242;
  line-height: 1.6;
  margin: 0;
}

.hh-success-link {
  color: #1976d2;
  font-weight: 500;
  white-space: nowrap;
  display: inline;
  text-decoration: none;

  > span {
    text-decoration: underline;
  }

  &:hover {
    > span {
      text-decoration: underline;
    }
  }
}

.hh-all-imported-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 10rem);
  padding: 2rem;
}

.hh-all-imported-card {
  width: 420px;
  border-radius: 8px;
  background: #F5F5F5;
  padding: 20px 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: none !important;
}

.hh-all-imported-icon {
  margin-bottom: 20px;
  width: 50px;
  height: 50px;
  background-color: rgba(31, 197, 127, 0.1);
  border-radius: 50%;
  padding: 12px;
  box-sizing: border-box;
}

.hh-all-imported-title {
  font-size: 24px;
  font-weight: 400;
  line-height: 110%;
  color: #212121;
  margin: 0 0 20px 0;
}

.hh-all-imported-text {
  font-size: 16px;
  font-weight: 300;
  line-height: 140%;
  color: #212121;
  margin: 0;
}

.hh-all-imported-link {
  color: #1976d2;
  font-weight: 500;
  white-space: nowrap;
  display: inline;
  text-decoration: none;

  > span {
    text-decoration: underline;
  }

  &:hover {
    > span {
      text-decoration: underline;
    }
  }
}

.hh-import-loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 10rem);
  padding: 2rem;
}

.hh-import-header-section {
  margin-bottom: 1.25rem;
}

.hh-import-mobile-info {
  display: none;
}

.hh-import-title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #091A35;
  margin-bottom: 1.25rem;
  text-align: center;
}

.hh-import-buttons {
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.hh-import-btn {
  min-width: 200px;
}

.hh-import-content {
  margin-top: 1.25rem;
}

.hh-import-card {
  border-radius: 10px;
  border: 1px dashed rgba(221, 221, 221, 1);
  box-shadow: none !important;
  height: calc(100vh - 170px);
  padding: 20px;

  @media screen and (max-width: 768px) {
    padding: 0;
  }

  ::v-deep .v-card__text {
    padding: 0;
  }

  ::v-deep .v-card__title {
    padding: 0;
  }
}

.hh-import-card-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #091A35;
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
}

.hh-import-select-section {
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.hh-import-select-all {
  display: flex;
  align-items: center;
}

.hh-import-selected-count {
  font-size: 0.875rem;
  color: rgba(9, 26, 53, 0.7);
  font-weight: 500;
}

.hh-vacancy-list {
  max-height: calc(100vh - 170px - 170px);
  overflow-y: auto;
}

.hh-vacancy-item {
  display: flex;
  align-items: center;
  background-color: rgba(245, 245, 245, 1);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 8px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &.hh-vacancy-item-selected {
    background-color: rgba(230, 242, 251, 1);
  }

  &.hh-vacancy-item-imported {
    background-color: rgba(245, 245, 245, 1);
    opacity: 1;
  }
}

.hh-vacancy-checkbox {
  flex-shrink: 0;
  margin-right: 10px;
  margin-top: 0;
}

.hh-vacancy-info {
  flex: 1;
  min-width: 0;
}

.hh-vacancy-title {
  font-size: 18px;
  font-weight: 400;
  line-height: 20px;
  color: rgba(32, 32, 32, 1);
  margin-bottom: 6px;
}

.hh-vacancy-item-selected .hh-vacancy-title {
  color: rgba(10, 130, 217, 1);
}

.hh-vacancy-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}

.hh-vacancy-detail-item {
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  color: rgba(33, 33, 33, 0.4);
  white-space: nowrap;
}

.hh-vacancy-imported-text {
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  color: rgba(33, 33, 33, 0.4);
  margin: 0 6px;
  white-space: nowrap;
}

.hh-vacancy-imported-icon {
  width: 20px;
  height: 20px;
  background-color: rgba(170, 170, 170, 1);
  border-radius: 50%;
  padding: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 12px;
    height: 12px;
    filter: brightness(0) invert(1);
    display: block;
  }
}

.hh-import-info-card {
  border-radius: 10px;
  border: 1px solid rgba(221, 221, 221, 1);
  box-shadow: none !important;
  height: calc(100vh - 170px);
  padding: 20px;
  overflow-y: auto;

  ::v-deep .v-card__text {
    padding: 0;
  }
}

.hh-info-section {
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.hh-info-section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #091A35;
  margin-bottom: 0.75rem;
}

.hh-info-section-text {
  font-size: 0.875rem;
  color: rgba(9, 26, 53, 0.7);
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.hh-info-section-instruction {
  font-size: 0.875rem;
  color: rgba(9, 26, 53, 0.7);
  line-height: 1.5;
  font-style: italic;
}

.hh-info-general-text {
  font-size: 0.875rem;
  color: rgba(9, 26, 53, 0.7);
  line-height: 1.5;
  margin-bottom: 2rem;
}

.hh-info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.hh-info-list-item {
  font-size: 0.875rem;
  color: rgba(9, 26, 53, 0.7);
  line-height: 1.6;
  margin-bottom: 0.75rem;
  padding-left: 1.25rem;
  position: relative;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: rgba(9, 26, 53, 0.5);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

@media screen and (max-width: 960px) {
  .hh-import-title {
    font-size: 1.25rem;
  }

  .hh-import-btn {
    min-width: 100%;
  }
}

@media screen and (max-width: 768px) {
  .hh-import-title {
    text-align: left;
    margin-bottom: 24px;
  }

  .hh-import-buttons {
    margin-bottom: 20px;
  }

  .hh-import-mobile-info {
    display: block;
    position: relative;
    margin-bottom: 1.25rem;
  }

  .hh-import-mobile-info-content {
    max-height: 200px;
    overflow: hidden;
    position: relative;
    padding: 20px;
    padding-bottom: 60px;
    background: #FFFFFF;
    border-radius: 10px;
    border: 1px solid rgba(221, 221, 221, 1);

    &.hh-import-mobile-info-expanded {
      max-height: none;
      padding-bottom: 20px;
    }
  }

  .hh-import-mobile-info-gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 76%);
    pointer-events: none;
    display: block;
  }

  .hh-import-mobile-info-expanded .hh-import-mobile-info-gradient {
    display: none;
  }

  .hh-import-mobile-info-button-wrapper {
    position: absolute;
    bottom: 16px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    z-index: 1;
  }

  .hh-import-mobile-info-expanded .hh-import-mobile-info-button-wrapper {
    position: static;
    margin-top: 16px;
  }

  .hh-import-mobile-info-button {
    background: rgba(245, 245, 245, 1) !important;
    height: 28px !important;
    font-size: 14px !important;
    line-height: 20px !important;
    font-weight: 400 !important;
    color: rgba(33, 33, 33, 1) !important;
    padding: 4px 12px !important;
    border-radius: 50px !important;
    text-transform: none !important;
    box-shadow: none !important;
    min-width: auto !important;
  }

  .hh-import-right-col {
    display: none;
  }

  .hh-import-card {
    border: none;
  }

  .hh-import-content {
    margin-top: 30px;
  }
}
</style>

<i18n locale="en" lang="yaml">
  importVacanciesFromHH: Import vacancies from HeadHunter
  pageTitle: HeadHunter → Huntica
  selectAll: Select all
  noVacanciesFound: No vacancies found
  noVacanciesAvailable: No vacancies available for import
  errorLoadingVacancies: Error loading vacancies
  errorImportingVacancies: Error importing vacancies
  importSelected: Import selected
  importAll: Import all
  selectVacanciesTitle: Select vacancies for import from HeadHunter
  autoInviteCandidates: Automatically invite all candidates for an AI interview
  selectedCount: "Selected: {count}"
  candidatesCount: "Candidates: {count}"
  createdDate: Created
  dateNotAvailable: Date not available
  imported: Imported
  importSelectedSectionTitle: Import selected vacancies
  importSelectedSectionDescription: this function allows you to transfer selected vacancies from HeadHunter to the Huntica system along with the candidates who responded to them.
  importSelectedSectionInstruction: To import the necessary vacancies, mark the desired positions in the list and click the "Import selected vacancies" button.
  importAllSectionTitle: Import all
  importAllSectionDescription: this function allows you to transfer all existing vacancies from HeadHunter to the Huntica system, as well as automatically import vacancies that will be created in the future.
  generalStatement: Along with vacancies, candidates who responded to each of these vacancies are imported.
  generalInfoTitle: General information
  generalInfoPoint1: All vacancies imported from HeadHunter to Huntica will be added to the system as drafts.
  generalInfoPoint2: This allows you to pre-approve or edit the list of questions for a future interview before sending them for voice-over using AI.
  generalInfoPoint3: After the voice-over of the questions is completed, you will be able to invite candidates for a video interview. Upon completion of the video interview, a brief resume and evaluation, generated with the help of AI, will be available for each candidate.
  generalInfoPoint4: All new candidates for each vacancy will be automatically imported from HeadHunter to Huntica.
  importSuccessTitle: Your vacancies have been imported
  importSuccessText: Currently they are in "Draft" status, follow the
  importSuccessLink: link to the vacancies page
  importSuccessTextSuffix: to publish the vacancies.
  allVacanciesImported: All vacancies imported
  vacancyImported: Vacancy imported
  vacanciesImported: Vacancies imported
  allImportedTitle: Your vacancies have been imported
  allImportedText: Currently they are in "Draft" status, follow the
  allImportedLink: link to the vacancies page
  allImportedTextSuffix: to publish the vacancies.
  showMore: Show more
  showLess: Show less
  attachToExisting: Attach to existing vacancy
  attachToExistingTitle: Attach HH vacancy to existing Huntica vacancy
  selectCabinetVacancy: Select Huntica vacancy
  selectHHVacancy: Select HeadHunter vacancy
  cancel: Cancel
  attach: Attach
  vacancyAlreadyHasIntegration: This vacancy already has HH integration
  hhVacancyAlreadyAttached: This HH vacancy is already attached
  vacancyAttachedSuccessfully: The HeadHunter vacancy has been successfully attached to the existing Huntica vacancy. Candidates will now be automatically imported.
  errorAttachingVacancy: Error attaching vacancy
</i18n>

<i18n locale="ru" lang="yaml">
  importVacanciesFromHH: Импорт вакансий из HeadHunter
  pageTitle: HeadHunter → Huntica
  selectAll: Выбрать все
  noVacanciesFound: Вакансии не найдены
  noVacanciesAvailable: Нет доступных вакансий для импорта
  errorLoadingVacancies: Ошибка загрузки вакансий
  errorImportingVacancies: Ошибка импорта вакансий
  importSelected: Импортировать выбранное
  importAll: Импортировать все
  selectVacanciesTitle: Выберите вакансии для импорта из HeadHunter
  autoInviteCandidates: Приглашать всех кандидатов на собеседование с ИИ автоматически
  selectedCount: "Выбрано: {count}"
  candidatesCount: "Кандидатов: {count}"
  createdDate: Создано
  dateNotAvailable: Дата недоступна
  imported: Импортировано
  importSelectedSectionTitle: Импортировать выбранные вакансии
  importSelectedSectionDescription: эта функция позволяет перенести выбранные вакансии из HeadHunter в систему Huntica вместе с кандидатами, откликнувшимися на них.
  importSelectedSectionInstruction: Чтобы импортировать необходимые вакансии, отметьте нужные позиции в списке и нажмите кнопку «Импортировать выбранные вакансии».
  importAllSectionTitle: Импортировать все
  importAllSectionDescription: данная функция позволяет перенести в систему Huntica все существующие вакансии из HeadHunter, а также автоматически импортировать вакансии, которые будут созданы в будущем.
  generalStatement: Вместе с вакансиями импортируются и кандидаты, откликнувшиеся на каждую из этих вакансий.
  generalInfoTitle: Общая информация
  generalInfoPoint1: Все вакансии, импортируемые из HeadHunter в Huntica, будут добавлены в систему в виде черновиков.
  generalInfoPoint2: Это позволяет предварительно согласовать или отредактировать список вопросов для будущего интервью, прежде чем отправлять их на озвучивание с помощью ИИ.
  generalInfoPoint3: После завершения озвучивания вопросов вы сможете приглашать кандидатов на видеоинтервью. По завершении видеоинтервью для каждого кандидата будет доступно краткое резюме и оценка, сформированные с помощью ИИ.
  generalInfoPoint4: Все новые кандидаты к каждой вакансии будут импортированы из HeadHunter в Huntica автоматически.
  importSuccessTitle: Ваши вакансии импортированы
  importSuccessText: Сейчас они в статусе "Черновик", перейдите по
  importSuccessLink: ссылке на страницу вакансий
  importSuccessTextSuffix: для того, чтобы опубликовать вакансии.
  allVacanciesImported: Все вакансии импортированы
  vacancyImported: Вакансия импортирована
  vacanciesImported: Вакансии импортированы
  allImportedTitle: Ваши вакансии импортированы
  allImportedText: Cейчас они в статусе "Черновик", перейдите по
  allImportedLink: ссылке на страницу вакансий
  allImportedTextSuffix: для того, чтобы опубликовать вакансии.
  showMore: Показать еще
  showLess: Скрыть
  attachToExisting: Прикрепить к существующей вакансии
  attachToExistingTitle: Прикрепить вакансию HH к существующей вакансии Huntica
  selectCabinetVacancy: Выберите вакансию Huntica
  selectHHVacancy: Выберите вакансию HeadHunter
  cancel: Отмена
  attach: Прикрепить
  vacancyAlreadyHasIntegration: У этой вакансии уже есть интеграция с HH
  hhVacancyAlreadyAttached: Эта вакансия HH уже прикреплена
  vacancyAttachedSuccessfully: Вакансия HeadHunter успешно прикреплена к существующей вакансии Huntica. Кандидаты будут автоматически импортироваться.
  errorAttachingVacancy: Ошибка при прикреплении вакансии
</i18n>
